import { useCallback, useEffect, useRef, useState } from "react";
import { training } from "services/api";
import { AssignedCourseSummary, CourseProgressData } from "types";
import { preloadCourseImages } from "utils/courseImagePreload";

export interface MyTrainingSummary {
  all_course_progress: Record<number, CourseProgressData>;
  assigned_courses: AssignedCourseSummary[];
  default_course_id: number | null;
  course_errors?: Record<number, unknown>;
  course_error?: unknown;
}

export const useMyTrainingSummary = (courseId?: number) => {
  const [data, setData] = useState<MyTrainingSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [settledCourseId, setSettledCourseId] = useState<number | null | undefined>(undefined);
  const isMountedRef = useRef(true);
  const latestRequestRef = useRef(0);
  const latestSummaryRef = useRef<MyTrainingSummary | null>(null);
  const librarySummaryRef = useRef<MyTrainingSummary | null>(null);
  const outlinesRef = useRef<Record<number, CourseProgressData>>({});
  const cachedSummary = useCallback(() => {
    const cached = courseId === undefined ? librarySummaryRef.current : latestSummaryRef.current;
    if (!cached || (courseId !== undefined && !outlinesRef.current[courseId])) return null;
    return {
      ...cached,
      assigned_courses: latestSummaryRef.current?.assigned_courses ?? cached.assigned_courses,
      all_course_progress: outlinesRef.current,
    };
  }, [courseId]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      latestRequestRef.current += 1;
    };
  }, []);

  const fetchSummary = useCallback(async (refresh = false) => {
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    try {
      const result = await training.summary(courseId === undefined ? { view: "library" } : { course_id: courseId });
      const typedResult = result as MyTrainingSummary;
      if (isMountedRef.current && requestId === latestRequestRef.current) {
        if (refresh) {
          outlinesRef.current = {};
          librarySummaryRef.current = null;
        }
        // Library progress contains only its full resume outline. Card summaries
        // never enter this cache and cannot stand in for an unopened curriculum.
        outlinesRef.current = { ...outlinesRef.current, ...typedResult.all_course_progress };
        latestSummaryRef.current = typedResult;
        if (courseId === undefined) librarySummaryRef.current = typedResult;
        for (const progress of Object.values(typedResult.all_course_progress ?? {})) {
          preloadCourseImages(progress);
        }
        setData(typedResult);
        setError(null);
      }
      return typedResult;
    } catch (err) {
      console.error("Failed to load My Training summary:", err);
      if (isMountedRef.current && requestId === latestRequestRef.current) {
        setError("Failed to load training data");
      }
      throw err;
    } finally {
      if (isMountedRef.current && requestId === latestRequestRef.current) {
        setLoading(false);
        setSettledCourseId(courseId ?? null);
      }
    }
  }, [courseId]);

  useEffect(() => {
    const cached = cachedSummary();
    if (cached) {
      latestRequestRef.current += 1;
      setData(cached);
      setLoading(false);
      setError(null);
      setSettledCourseId(courseId ?? null);
      return;
    }
    fetchSummary().catch(() => null);
  }, [cachedSummary, courseId, fetchSummary]);

  const refetch = useCallback(() => fetchSummary(true), [fetchSummary]);
  const changingScope = settledCourseId !== (courseId ?? null);
  const cached = changingScope ? cachedSummary() : null;

  return {
    data: cached ?? data,
    loading: changingScope ? !cached : loading,
    error,
    refetch,
  };
};

export default useMyTrainingSummary;
